import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'text-foreground',
        success: 'border-transparent bg-green-500 text-white hover:bg-green-600',
        warning: 'border-transparent bg-yellow-500 text-white hover:bg-yellow-600',
        info: 'border-transparent bg-blue-500 text-white hover:bg-blue-600',
        glass: 'border-white/20 bg-white/10 backdrop-blur-md text-white hover:bg-white/20',
      },
      size: {
        default: 'h-5',
        sm: 'h-4 text-[10px]',
        lg: 'h-6 text-sm',
      },
      animation: {
        none: '',
        pulse: 'animate-pulse',
        bounce: 'animate-bounce',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      animation: 'none',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  dot?: boolean;
  glow?: boolean;
}

function Badge({ className, variant, size, animation, dot, glow, children, ...props }: BadgeProps) {
  return (
    <div
      className={cn(badgeVariants({ variant, size, animation }), glow && 'shadow-glow', className)}
      {...props}
    >
      {dot && (
        <span
          className={cn(
            'mr-1 h-1.5 w-1.5 rounded-full',
            variant === 'default' && 'bg-primary-foreground',
            variant === 'secondary' && 'bg-secondary-foreground',
            variant === 'destructive' && 'bg-destructive-foreground',
            variant === 'outline' && 'bg-foreground',
            variant === 'success' && 'bg-white',
            variant === 'warning' && 'bg-white',
            variant === 'info' && 'bg-white',
            variant === 'glass' && 'bg-white'
          )}
        />
      )}
      {children}
    </div>
  );
}

export { Badge, badgeVariants };
